import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Columns3, X } from "lucide-react";
import { inr } from "@/lib/format";
import { BottomSheet } from "@/components/BottomSheet";
import { ROOM_AMENITY } from "@/components/RoomAmenities";

const sharingLabel = (s: number) => (s === 1 ? "Single" : `${s}-sharing`);
const MAX_COMPARE = 3;

export function useCompare() {
  const [picked, setPicked] = useState<string[]>([]);
  const toggle = (id: string) => setPicked((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= MAX_COMPARE) { toast.error(`You can compare up to ${MAX_COMPARE} rooms`); return s; }
    return [...s, id];
  });
  return { picked, toggle, clear: () => setPicked([]) };
}

export function CompareBar({ count, onClear, onOpen }: { count: number; onClear: () => void; onOpen: () => void }) {
  if (!count) return null;
  return (
    <div className="sticky bottom-24 z-20 flex justify-center pg-rise">
      <div className="flex items-center gap-2 rounded-full bg-[#131e2e] text-white shadow-lg pl-4 pr-1.5 py-1.5" data-testid="compare-bar">
        <span className="text-sm font-semibold">{count} selected</span>
        <button data-testid="compare-clear-btn" onClick={onClear} className="text-xs text-white/70 underline px-1">Clear</button>
        <button data-testid="compare-open-btn" disabled={count < 2} onClick={onOpen}
          className="h-10 px-4 rounded-full bg-[#fccc00] text-[#131e2e] text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"><Columns3 className="h-4 w-4" />Compare</button>
      </div>
    </div>
  );
}

function Row({ label, rooms, render, testId }: { label: string; rooms: any[]; render: (r: any) => React.ReactNode; testId: string }) {
  return (
    <tr className="border-t border-slate-100" data-testid={testId}>
      <th className="py-2.5 pr-2 text-left text-xs font-semibold text-slate-500 align-top w-24">{label}</th>
      {rooms.map((r) => <td key={r.id} className="py-2.5 px-1 text-sm text-center align-top">{render(r)}</td>)}
    </tr>
  );
}

export function RoomCompareTable({ rooms, onRequest }: { rooms: any[]; onRequest?: (r: any) => void }) {
  const minRent = Math.min(...rooms.map((r) => r.rent_per_bed));
  return (
      <div className="overflow-x-auto -mx-1" data-testid="room-compare-table">
        <table className="w-full table-fixed" style={{ minWidth: rooms.length > 3 ? 96 + rooms.length * 110 : undefined }}>
          <thead>
            <tr>
              <th className="w-24" />
              {rooms.map((r) => <th key={r.id} className="pb-2 px-1 text-sm font-extrabold text-[#131e2e]" data-testid={`compare-head-${r.name}`}>Room {r.name}{r.pg_name ? <span className="block text-[11px] font-semibold text-[#3d52a0] truncate" data-testid={`compare-pg-${r.name}`}>{r.pg_name}</span> : null}{r.floor ? <span className="block text-[11px] font-semibold text-slate-500">Floor {r.floor}</span> : null}</th>)}
            </tr>
          </thead>
          <tbody>
            <Row testId="compare-rent" label="Rent / bed" rooms={rooms} render={(r) => <span className={`font-mono-num font-bold ${r.rent_per_bed === minRent && rooms.length > 1 ? "text-green-700" : ""}`}>{inr(r.rent_per_bed)}</span>} />
            <Row testId="compare-deposit" label="Deposit" rooms={rooms} render={(r) => <span className="font-mono-num">{inr(r.deposit)}</span>} />
            <Row testId="compare-sharing" label="Sharing" rooms={rooms} render={(r) => sharingLabel(r.sharing)} />
            <Row testId="compare-free" label="Free beds" rooms={rooms} render={(r) => <span className={r.vacant_beds ? "font-semibold text-green-700" : "text-slate-400"}>{r.vacant_beds ? `${r.vacant_beds} of ${r.sharing}` : "Full"}</span>} />
            {Object.entries(ROOM_AMENITY).map(([k, label]) => (
              <Row key={k} testId={`compare-feature-${k}`} label={label} rooms={rooms} render={(r) => (r.amenities || []).includes(k)
                ? <Check className="h-4 w-4 mx-auto text-green-600" aria-label="Yes" /> : <X className="h-4 w-4 mx-auto text-slate-300" aria-label="No" />} />
            ))}
            {onRequest && (
              <tr className="border-t border-slate-100">
                <th />
                {rooms.map((r) => (
                  <td key={r.id} className="pt-3 px-1">
                    {r.vacant_beds === 0 && <p className="text-xs text-center text-slate-400">Full</p>}
                    {r.vacant_beds > 0 && <button data-testid={`compare-request-${r.name}`} onClick={() => onRequest(r)} className="w-full h-10 rounded-xl bg-[#131e2e] text-white text-xs font-semibold pg-tap hover:bg-[#0b1320]">Request</button>}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
  );
}

export function RoomCompareSheet({ rooms, open, onClose, onRequest }: { rooms: any[]; open: boolean; onClose: () => void; onRequest?: (r: any) => void }) {
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title="Compare rooms" description={`${rooms.length} rooms side by side`} testId="room-compare-sheet">
      <RoomCompareTable rooms={rooms} onRequest={onRequest && ((r) => { onClose(); onRequest(r); })} />
    </BottomSheet>
  );
}
